/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { getAxiosForClient } from '@/lib/axios';
import type { FormattedContentStackEnhancedPDP } from '@/features/contentstack/interface';
import { getContentStackEnhancedPDPContent } from '@/features/contentstack/lib/productDetailsPage/getEnhancedContent'; // eslint-disable-line no-restricted-imports
import { type PageContextType } from '@/next-types';
import {
  type QueryClient,
  useQuery,
  type QueryFunctionContext,
  type UseQueryOptions,
  keepPreviousData,
} from '@tanstack/react-query';
import { useSegments } from '@/features/header/api/getSegments';

const URL = `/api/contentstack/productDetailsPages/enhanced`;

type Options = {
  taxonomyPath: string;
  brandName?: string;
  sku?: string;
};

const getContentStackEnhancedProductContent = async ({
  queryKey,
}: QueryFunctionContext<ReturnType<typeof getEnhancedPDPKey>>) => {
  const [, , , { segments, taxonomyPath: rawTaxonomyPath, brandName, sku }] = queryKey;
  // NOTE: this is needed due to https://github.com/TanStack/query/issues/1196 (despite setting the enabled option to false, useQuery still initiates a fetch)
  // Allow requests with taxonomyPath only (for taxonomy-only matching)
  if (!rawTaxonomyPath) {
    return null;
  }
  // Strip /b/brand/... filter segments — keep only the base taxonomy path
  const taxonomyPath = rawTaxonomyPath.replace(/\/b\/.*$/, '');
  const response = await getAxiosForClient().get<FormattedContentStackEnhancedPDP>(URL, {
    params: {
      segments: segments.join(','),
      taxonomyPath,
      brandName,
      sku: sku || '',
    },
  });
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return selector(response.data);
};

const selector = (data: FormattedContentStackEnhancedPDP) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return data;
};

export const getEnhancedPDPKey = (
  taxonomyPath: string,
  segments: string[],
  brandName?: string,
  sku?: string,
) => {
  // Canonicalize segments by sorting to ensure consistent cache keys
  const sortedSegments = [...segments].sort();
  return [
    'cms',
    'enhanced',
    'pdp',
    { segments: sortedSegments, taxonomyPath, brandName, sku },
  ] as const;
};

export const useContentStackEnhancedPDPContent = (
  options: Omit<
    UseQueryOptions<
      FormattedContentStackEnhancedPDP,
      unknown,
      FormattedContentStackEnhancedPDP,
      ReturnType<typeof getEnhancedPDPKey>
    >,
    'queryKey' | 'queryFn'
  > &
    Options,
) => {
  const { data: segments, isSuccess } = useSegments();
  const { taxonomyPath, brandName, sku, enabled = true } = options;
  // Allow requests with taxonomyPath only (for taxonomy-only matching)
  const isEnabled = enabled && !!taxonomyPath && isSuccess;
  const queryResult = useQuery({
    queryFn: getContentStackEnhancedProductContent,
    queryKey: getEnhancedPDPKey(taxonomyPath, segments ?? [], brandName, sku),
    enabled: isEnabled,
    placeholderData: keepPreviousData,
  });

  return {
    ...queryResult,
    isPending: isEnabled ? queryResult.isPending : false,
  };
};

export const prefetchContentStackEnhancedPDPContent = (
  queryClient: QueryClient,
  taxonomyPath: string,
  context: PageContextType,
  userSegments: string[],
  brandName?: string,
  sku?: string,
) => {
  return queryClient.prefetchQuery({
    queryKey: getEnhancedPDPKey(taxonomyPath, userSegments, brandName, sku),
    queryFn: () =>
      getContentStackEnhancedPDPContent(context.req, {
        ...context.query,
        taxonomyPath,
        brandName,
        sku,
      }),
  });
};

export const getContentStackEnhancedPDPContentFromCache = (
  queryClient: QueryClient,
  taxonomyPath: string,
  userSegments: string[],
  brandName?: string,
  sku?: string,
) => {
  // eslint-disable-next-line @typescript-eslint/no-unsafe-return
  return queryClient.getQueryData<FormattedContentStackEnhancedPDP>(
    getEnhancedPDPKey(taxonomyPath, userSegments, brandName, sku),
  );
};
